import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import axios from 'axios';
import { CompanyStatus, CourtProcedureType, PartyType, RepresentativeType } from 'src/types';

export type CompanySearchAutocompleteItem = {
  name: string;
  short_name?: string | null;
  country: {
    currency_iso: string | null;
    id: number;
    iso: string;
    name: string | null;
  } | null;
  party_type: {
    id: number;
    internal_id: string;
    label: string;
    match_reference: string[];
    name: string;
    registration_office_id: number;
    type: PartyTypeType;
  } | null;
  postcode: string;
  settlement: string;
  street: string;
  tax_number: string;
  registration_number?: string | null;
  registration_office_id?: number | null;
  status?: CompanyStatus;
  court_procedure_type?: CourtProcedureType | null;
};

type Address = {
  country_id: number | null;
  settlement: string | null;
  postcode: string | null;
  street: string | null;
};

type Representative = {
  name: string | null;
  tax_number: string | null;
  is_standalone: boolean;
  id: number;
  parent_id: number | null;
  representative_data: {
    birth_date: string | null;
    birth_place: string | null;
    mother_name: string | null;
    registration_number: string | null;
  };
  representativeType: RepresentativeType | null;
  partyType: PartyType | null;
  registrationOffice: {
    internal_id: string;
    name: string;
  } | null;
  address: Address | null;
  children: Representative[];
};

export type CompanySearchBankAccountItem = {
  bic?: string | null;
  bank_name?: string | null;
  label?: string | null;
  account_number?: string | null;
  iban?: string | null;
  account_holder_name: string;
};

export type CompanySearchDetailedItem = {
  name: string | null;
  long_name: string | null;
  registration_number: string | null;
  tax_number: string | null;
  partyType: PartyType | null;
  status: CompanyStatus;
  representatives: Representative[];
  bankAccounts: CompanySearchBankAccountItem[];
  contact: null | {
    type?: string;
    value: string | null;
  };
  address: Address | null;
};

export type PartyTypeType = 'org' | 'ind';

@Injectable({
  providedIn: 'root'
})
export class CompanySearchService {
  // tslint:disable-next-line
  private _autocompleteSearchLoading = 0;
  get autocompleteSearchLoading() { return !!this._autocompleteSearchLoading; }

  // tslint:disable-next-line
  private _detailedSearchLoading = 0;
  get detailedSearchLoading() { return !!this._detailedSearchLoading; }

  async exactSearch(
    taxNumber: string | null | undefined,
  ): Promise<CompanySearchAutocompleteItem | null> {
    if (!taxNumber || taxNumber.replace(/\D/g, '').length < 8) {
      return null;
    }

    try {
      ++this._autocompleteSearchLoading;
      const url = `${environment.baseUrl}/api/company-search/quick-exact?tax_number=${encodeURIComponent(taxNumber ?? '')}`;
      const result = await axios.get<{ item: CompanySearchAutocompleteItem | null; }>(url);
      return result.data.item;
    } finally {
      --this._autocompleteSearchLoading;
    }
  }

  async getCompanySearchAutocompleteItems(searchString: string | null | undefined): Promise<CompanySearchAutocompleteItem[]> {
    if (!searchString || searchString.length < 3) {
      return [];
    }

    try {
      ++this._autocompleteSearchLoading;
      const url = `${environment.baseUrl}/api/company-search/autocomplete?searchString=${encodeURIComponent(searchString)}`;
      const result = await axios.get<{ items: CompanySearchAutocompleteItem[]; }>(url);
      return result.data.items;
    } finally {
      --this._autocompleteSearchLoading;
    }
  }

  async detailedSearch(searchString: string): Promise<{ company: CompanySearchDetailedItem; } | null> {
    if (!searchString || searchString.length < 3) {
      return null;
    }

    try {
      ++this._detailedSearchLoading;
      const url = `${environment.baseUrl}/api/company-search/detailed?searchString=${encodeURIComponent(searchString)}`;
      const result = await axios.get<{
        company: CompanySearchDetailedItem & {
          bankAccount?: CompanySearchBankAccountItem | null;
        };
      }>(url);
      const company = result.data.company;
      const bankAccounts = company.bankAccounts?.length
        ? company.bankAccounts
        : company.bankAccount
          ? [company.bankAccount]
          : [];

      return {
        company: {
          ...company,
          bankAccounts,
        },
      };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }
      throw error;
    } finally {
      --this._detailedSearchLoading;
    }
  }

  async getRepresentatives(taxNumber: string | null | undefined): Promise<{ representatives: Representative[]; } | null> {
    if (!taxNumber || taxNumber.length < 3) {
      return null;
    }

    const url = `${environment.baseUrl}/api/company-search/representatives`;
    const result = await axios.get<{ representatives: Representative[]; }>(url, {
      params: {
        tax_number: taxNumber,
      }
    });
    return result.data;
  }
}
